/**
 * Seat Registry Page
 * Elvis Aloha Collection - Interactive Seat Map
 * 
 * This page displays the interactive SVG seat map with:
 * - Two layout view modes (Concert Reconstruction / Venue Reference)
 * - Ticket data layer filtering
 * - Seat search
 * - Blank tickets panel
 * - Ticket detail modals
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import ArenaExplorer from '../../components/ArenaExplorer';

const SeatRegistryPage = () => {
  const handleSeatSelect = (seat, ticket) => {
    console.log('Seat selected:', seat, ticket);
  };

  return (
    <>
      <Helmet>
        <title>Interactive Seat Registry | Elvis Aloha Collection</title>
        <meta name="description" content="Explore the interactive seat map of the Neal S. Blaisdell Arena for the January 14, 1973 Aloha from Hawaii concert. View documented tickets by section, row, and seat." />
        <meta property="og:title" content="Interactive Seat Registry | Elvis Aloha Collection" />
        <meta property="og:description" content="Explore the documented ticket collection mapped to the original 1973 concert venue configuration." />
      </Helmet>

      <main 
        data-testid="seat-registry-page"
        style={{ 
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: '40px 24px',
          minHeight: '100vh'
        }}
      >
        {/* Page Header */}
        <header style={{ marginBottom: '40px' }}>
          <div style={{ 
            fontFamily: "'Source Sans 3', sans-serif", 
            fontSize: '12px', 
            color: '#C9A962',
            fontWeight: 600,
            letterSpacing: '0.1em',
            marginBottom: '12px'
          }}>
            ELVIS ALOHA COLLECTION
          </div>
          
          <h1 style={{ 
            margin: '0 0 16px', 
            fontFamily: "'Cormorant Garamond', serif", 
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: 500,
            color: '#1a1a1a',
            lineHeight: 1.2
          }}>
            Interactive Aloha Ticket Seat Registry
          </h1>
          
          <p style={{ 
            margin: 0, 
            fontFamily: "'Source Sans 3', sans-serif", 
            fontSize: '16px',
            color: '#666',
            lineHeight: 1.6,
            maxWidth: '800px'
          }}>
            The June 21, 2021 inventory documents the original collection baseline of <strong>252 tickets</strong>: 
            136 seat-numbered tickets distributed across Sections AA, BB, H, and L, plus 116 blank tickets. 
            The collection has expanded through later acquisitions.
          </p>
        </header>

        {/* Graceland Context */}
        <section 
          style={{
            padding: '20px 24px',
            background: '#f8f8f8',
            borderRadius: '8px',
            marginBottom: '32px',
            borderLeft: '4px solid #C9A962'
          }}
        >
          <h2 style={{ 
            margin: '0 0 12px', 
            fontFamily: "'Source Sans 3', sans-serif", 
            fontSize: '14px',
            fontWeight: 700,
            color: '#7a6832'
          }}>
            Graceland Collection Scope
          </h2>
          <p style={{ 
            margin: 0, 
            fontFamily: "'Source Sans 3', sans-serif", 
            fontSize: '14px',
            color: '#666',
            lineHeight: 1.6
          }}>
            The complete original collection was provided to Graceland on loan and remained in the custody of the 
            Graceland Archives and museum for nearly three years. <strong>Fifty tickets</strong> were selected from 
            the larger collection for the public Aloha display alongside the jumpsuit. 
            Individual display-ticket identities are being documented.
          </p>
        </section>

        {/* Interactive Arena Explorer */}
        <ArenaExplorer 
          onSeatSelect={handleSeatSelect}
          isOwnerMode={false}
        />

        {/* Blank Tickets Section */}
        <BlankTicketsPanel />

        {/* Methodology */}
        <MethodologySection />
      </main>
    </>
  );
};

const BlankTicketsPanel = () => {
  const [blanks, setBlanks] = React.useState({ tickets: [], total: 0 });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchBlanks = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seat-map/tickets/blank`);
        const data = await res.json();
        setBlanks(data);
      } catch (err) {
        console.error('Failed to fetch blank tickets:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlanks();
  }, []);

  return (
    <section 
      data-testid="blank-tickets-panel"
      style={{
        padding: '24px 28px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginTop: '40px',
        border: '1px solid #eee'
      }}
    >
      <h2 style={{ 
        margin: '0 0 16px', 
        fontFamily: "'Cormorant Garamond', serif", 
        fontSize: '26px',
        fontWeight: 500,
        color: '#1a1a1a'
      }}>
        Blank and Unassigned Tickets
      </h2>
      
      <p style={{ 
        margin: '0 0 20px', 
        fontFamily: "'Source Sans 3', sans-serif", 
        fontSize: '14px',
        color: '#666',
        lineHeight: 1.6,
        maxWidth: '720px'
      }}>
        {loading ? 'Loading...' : blanks.description || `${blanks.total} blank or unassigned tickets in the original inventory`}. 
        These tickets do not have printed seat assignments and cannot be mapped to specific arena locations. 
        Each ticket supports individual catalog records including certification, photographs, condition, provenance, 
        Graceland custody, and exhibition history where documented.
      </p>
      
      {!loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '16px 20px',
          background: '#fafafa',
          borderRadius: '8px'
        }}>
          <div style={{ 
            fontFamily: "'Cormorant Garamond', serif", 
            fontSize: '36px', 
            fontWeight: 500, 
            color: '#C9A962' 
          }}>
            {blanks.total}
          </div>
          <div>
            <div style={{ 
              fontFamily: "'Source Sans 3', sans-serif", 
              fontSize: '14px', 
              fontWeight: 600,
              color: '#1a1a1a' 
            }}>
              Blank Tickets
            </div>
            <div style={{ 
              fontFamily: "'Source Sans 3', sans-serif", 
              fontSize: '12px', 
              color: '#888' 
            }}>
              Original 2021 inventory
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const MethodologySection = () => (
  <section 
    style={{
      marginTop: '48px',
      padding: '28px',
      background: '#fafafa',
      borderRadius: '12px',
      border: '1px solid #eee'
    }}
  >
    <h3 style={{ 
      margin: '0 0 16px', 
      fontFamily: "'Source Sans 3', sans-serif", 
      fontSize: '14px',
      fontWeight: 700,
      color: '#888',
      letterSpacing: '0.05em'
    }}>
      HOW THIS RECONSTRUCTION WAS CREATED
    </h3>
    <p style={{ 
      margin: '0 0 12px', 
      fontFamily: "'Source Sans 3', sans-serif", 
      fontSize: '14px',
      color: '#666',
      lineHeight: 1.7
    }}>
      The Neal S. Blaisdell Arena can be configured differently for concerts, sports, and other events. 
      Its published seating charts represent standard modern layouts. The January 1973 Aloha production 
      used a custom television stage with a runway, mirrored structures, specialized lighting, camera positions, 
      and altered audience seating.
    </p>
    <p style={{ 
      margin: '0 0 12px', 
      fontFamily: "'Source Sans 3', sans-serif", 
      fontSize: '14px',
      color: '#666',
      lineHeight: 1.7
    }}>
      This reconstruction combines venue geometry, production accounts, surviving footage, photographs, 
      and known ticket assignments. Confirmed and uncertain areas are identified separately. 
      The exact original seating chart has not yet been established.
    </p>
    <p style={{ 
      margin: 0, 
      fontFamily: "'Source Sans 3', sans-serif", 
      fontSize: '13px',
      color: '#888',
      lineHeight: 1.6,
      fontStyle: 'italic'
    }}>
      The original collection includes seat-numbered tickets assigned to areas affected by the custom Aloha stage configuration. 
      The seat assignments remained printed on the tickets even where the corresponding audience locations appear to have been 
      removed or obstructed for the production. Individual issue and use status is recorded according to the supporting evidence.
    </p>
  </section>
);

export default SeatRegistryPage;
